/* ============================================================
   custom-form.js: the shared "creative conversation" engine.
   Reads window.CUST26 (per-page config), renders a 6-step flow
   into #cf-root: Idea → Details → Budget → Timeline → You →
   Review, plus the "what happens next" screen.
   Mockup scope: client-side only. Upload previews via
   FileReader; state preserved in localStorage; submit simulated.
   ============================================================ */
(function () {
  'use strict';
  var CFG = window.CUST26 || {};
  var root = document.getElementById('cf-root');
  if (!root) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var KEY = 'ps-custom:' + (CFG.category || 'jewelry');
  var THUMB_CAP = 300 * 1024; // persisted thumbnails budget

  /* ---------------- state ---------------- */
  var state = {
    step: 0,
    images: [],            // {name, data} data = downscaled dataURL
    idea: '',
    matchImages: [],       // optional second slot (bands)
    details: {},           // key -> array of selected labels
    budget: '',
    occasion: '',
    date: '',
    name: '',
    email: '',
    phone: '',
    contact: 'Email',
    ref: '',               // PS-C-..., minted when the You step first opens
    eventId: ''            // ref + '.xxxx': the one id Meta/Google dedupe this lead on
  };
  var resumed = false;

  function save() {
    try {
      var imgs = [], total = 0;
      state.images.concat(state.matchImages).forEach(function (im) { total += (im.data || '').length; });
      var persistThumbs = total <= THUMB_CAP;
      var copy = JSON.parse(JSON.stringify(state, function (k, v) { return k === 'file' ? undefined : v; }));
      if (!persistThumbs) {
        copy.images = state.images.map(function (im) { return { name: im.name, data: '' }; });
        copy.matchImages = state.matchImages.map(function (im) { return { name: im.name, data: '' }; });
        copy._thumbsDropped = true;
      }
      localStorage.setItem(KEY, JSON.stringify(copy));
    } catch (e) { /* storage may be unavailable from file://, fine for the mockup */ }
  }
  var saveT;
  function saveSoon() { clearTimeout(saveT); saveT = setTimeout(save, 250); }

  /* ---------------- steps model ---------------- */
  var STEPS = [
    { id: 'idea', phase: 0 },
    { id: 'details', phase: 1 },
    { id: 'budget', phase: 1 },
    { id: 'timeline', phase: 1 },
    { id: 'you', phase: 2 },
    { id: 'review', phase: 3 }
  ];
  var PHASES = ['Idea', 'Details', 'You', 'Send'];

  /* ---------------- utilities ---------------- */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html !== undefined) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function ideaValid() {
    /* A preselected gallery design IS the idea: photos/text become optional refinements. */
    if (CFG.preset) return true;
    return state.images.length > 0 || state.idea.trim().length > 0;
  }
  function youValid() {
    return state.name.trim().length > 1 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim());
  }

  /* ---------------- chips ---------------- */
  function chipGroup(label, options, selectedArr, opts) {
    opts = opts || {};
    var g = el('fieldset', 'cf-group');
    g.appendChild(el('legend', null, esc(label)));
    var row = el('div', 'cf-chips');
    options.forEach(function (o) {
      var b = el('button', 'cf-chip' + (o === opts.quiet ? ' cf-chip--quiet' : ''), esc(o));
      b.type = 'button';
      b.setAttribute('aria-pressed', selectedArr.indexOf(o) > -1 ? 'true' : 'false');
      b.addEventListener('click', function () {
        var i = selectedArr.indexOf(o);
        if (opts.single) {
          selectedArr.length = 0;
          if (i === -1) selectedArr.push(o);
          Array.prototype.forEach.call(row.children, function (c) {
            c.setAttribute('aria-pressed', c === b && i === -1 ? 'true' : 'false');
          });
        } else {
          if (i > -1) selectedArr.splice(i, 1); else selectedArr.push(o);
          b.setAttribute('aria-pressed', i > -1 ? 'false' : 'true');
        }
        saveSoon();
      });
      row.appendChild(b);
    });
    g.appendChild(row);
    return g;
  }

  /* ---------------- upload ---------------- */
  function uploadBlock(list, hint, sub) {
    var wrap = el('div');
    var drop = el('div', 'cf-drop');
    drop.setAttribute('role', 'button');
    drop.setAttribute('tabindex', '0');
    drop.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.5" aria-hidden="true"><path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" stroke-linecap="round"/></svg>' +
      '<strong>' + esc(hint) + '</strong>' +
      '<span>' + esc(sub) + '</span>';
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.hidden = true;
    var thumbs = el('div', 'cf-thumbs');

    function renderThumbs() {
      thumbs.innerHTML = '';
      list.forEach(function (im, i) {
        var t = el('div', 'cf-thumb' + (im.loading ? ' loading' : ''));
        t.innerHTML = im.data
          ? '<img src="' + im.data + '" alt="' + esc(im.name) + '">'
          : '<div role="img" aria-label="' + esc(im.name) + '" style="width:100%;aspect-ratio:1/1;background:var(--ps-bg-soft)"></div>';
        var rm = el('button', 'rm', '&times;');
        rm.type = 'button';
        rm.setAttribute('aria-label', 'Remove ' + im.name);
        rm.addEventListener('click', function () { list.splice(i, 1); renderThumbs(); saveSoon(); refreshNav(); });
        t.appendChild(rm);
        thumbs.appendChild(t);
      });
    }

    function addFiles(files) {
      Array.prototype.forEach.call(files, function (f) {
        if (!/^image\//.test(f.type)) return;
        /* .file is the shopper's ORIGINAL upload, kept in memory only so the
         attachment endpoint can send the real photo; localStorage still gets
         just the 480px .data copy (see saveSoon). */
      var entry = { name: f.name, data: '', file: f, loading: true };
        list.push(entry);
        renderThumbs();
        var reader = new FileReader();
        reader.onload = function () {
          var img = new Image();
          img.onload = function () {
            var c = document.createElement('canvas');
            var s = Math.min(1, 480 / Math.max(img.width, img.height));
            c.width = Math.round(img.width * s);
            c.height = Math.round(img.height * s);
            c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
            /* brief simulated-upload beat so the feedback reads as real */
            setTimeout(function () {
              entry.data = c.toDataURL('image/jpeg', 0.7);
              entry.loading = false;
              renderThumbs(); saveSoon(); refreshNav();
            }, REDUCED ? 0 : 350 + Math.random() * 300);
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(f);
      });
    }

    drop.addEventListener('click', function () { input.click(); });
    drop.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); } });
    input.addEventListener('change', function () { addFiles(input.files); input.value = ''; });
    ['dragover', 'dragenter'].forEach(function (evn) {
      drop.addEventListener(evn, function (e) { e.preventDefault(); drop.classList.add('over'); });
    });
    ['dragleave', 'drop'].forEach(function (evn) {
      drop.addEventListener(evn, function (e) { e.preventDefault(); drop.classList.remove('over'); });
    });
    drop.addEventListener('drop', function (e) { if (e.dataTransfer) addFiles(e.dataTransfer.files); });

    wrap.appendChild(drop);
    wrap.appendChild(input);
    wrap.appendChild(thumbs);
    renderThumbs();
    return wrap;
  }

  /* ---------------- step builders ---------------- */
  function buildIdea(body) {
    if (CFG.preset) {
      var pc = el('div', 'cf-preset');
      pc.innerHTML =
        '<img src="' + esc(CFG.preset.image) + '" alt="' + esc(CFG.preset.alt || CFG.preset.name) + '" width="96" height="96">' +
        '<div class="cf-preset-txt"><span class="cf-preset-eyebrow">Your starting design</span>' +
        '<strong>' + esc(CFG.preset.name) + '</strong>' +
        (CFG.preset.subcategory ? '<span class="cf-preset-sub">' + esc(CFG.preset.subcategory) + '</span>' : '') +
        '</div>';
      body.appendChild(pc);
    }
    body.appendChild(uploadBlock(
      state.images,
      (CFG.step1 && CFG.step1.uploadHint) || 'Drop photos of your idea here',
      'Screenshots, Pinterest saves, sketches, an old photo, anything. Tap to browse on mobile.'
    ));
    if (CFG.step1 && CFG.step1.matchUpload) {
      var lbl = el('span', 'cf-upload-label', esc(CFG.step1.matchUpload));
      body.appendChild(lbl);
      body.appendChild(uploadBlock(state.matchImages, CFG.step1.matchUpload, 'A quick phone photo is perfect.'));
    }
    var f = el('div', 'cf-field');
    f.innerHTML = '<label for="cf-idea">' +
      esc((CFG.step1 && CFG.step1.textLabel) || 'Or just describe it in your own words') +
      ' <span class="opt">(either one is enough)</span></label>';
    var ta = document.createElement('textarea');
    ta.className = 'ps-input';
    ta.id = 'cf-idea';
    ta.placeholder = (CFG.step1 && CFG.step1.placeholder) || 'Tell us what you have in mind…';
    ta.value = state.idea;
    ta.addEventListener('input', function () { state.idea = ta.value; saveSoon(); refreshNav(); });
    f.appendChild(ta);
    body.appendChild(f);
    var err = el('p', 'cf-err');
    err.id = 'cf-idea-err';
    body.appendChild(err);
  }

  function buildDetails(body) {
    (CFG.details || []).forEach(function (q) {
      state.details[q.key] = state.details[q.key] || [];
      body.appendChild(chipGroup(q.label, q.options.concat(q.notSure === false ? [] : ['Not sure yet']), state.details[q.key], { single: !!q.single }));
    });
  }

  function buildBudget(body) {
    state._budgetArr = state.budget ? [state.budget] : [];
    var chips = (CFG.budgetChips || ['Under $1,500', '$1,500 – $3,000', '$3,000 – $5,000', '$5,000 – $10,000', '$10,000+']).concat(["I'm not sure yet, show me what's possible"]);
    var g = chipGroup('What would you like to invest?', chips, state._budgetArr, { single: true });
    g.addEventListener('click', function () { state.budget = state._budgetArr[0] || ''; saveSoon(); });
    body.appendChild(g);
  }

  function buildTimeline(body) {
    state._occArr = state.occasion ? [state.occasion] : [];
    var g = chipGroup('Is this for a moment that matters?', (CFG.occasions || ['A proposal', 'A wedding', 'An anniversary', 'A birthday', 'Just because', 'No rush']), state._occArr, { single: true });
    g.addEventListener('click', function () { state.occasion = state._occArr[0] || ''; saveSoon(); });
    body.appendChild(g);
    var f = el('div', 'cf-field');
    f.innerHTML = '<label for="cf-date">Is there a date it should be ready for? <span class="opt">(optional)</span></label>';
    var inp = document.createElement('input');
    inp.type = 'date';
    inp.className = 'ps-input';
    inp.id = 'cf-date';
    inp.value = state.date;
    var note = el('p', 'cf-help');
    function dateNote() {
      note.textContent = '';
      if (inp.value) {
        var days = Math.round((new Date(inp.value) - new Date()) / 86400000);
        if (days >= 0 && days < 21) note.textContent = 'That’s soon. Tell us anyway. Rush timelines are often possible, and we’ll confirm before you commit to anything.';
      }
    }
    inp.addEventListener('change', function () { state.date = inp.value; dateNote(); saveSoon(); });
    dateNote();
    f.appendChild(inp);
    body.appendChild(f);
    body.appendChild(note);
  }

  function buildYou(body) {
    ensureIds();
    var g2 = el('div', 'cf-grid2');
    g2.appendChild(field('cf-name', 'First name', 'text', 'name', 'Alex'));
    g2.appendChild(field('cf-email', 'Email', 'email', 'email', 'you@example.com'));
    body.appendChild(g2);
    var g3 = el('div', 'cf-grid2');
    g3.appendChild(field('cf-phone', 'Phone', 'tel', 'phone', 'Optional', true));
    var cw = el('div', 'cf-field');
    cw.innerHTML = '<label>How should we reach you?</label>';
    state._contactArr = [state.contact];
    var cg = chipGroup('', ['Email', 'Call', 'Text'], state._contactArr, { single: true });
    cg.querySelector('legend').remove();
    cg.addEventListener('click', function () { state.contact = state._contactArr[0] || 'Email'; saveSoon(); });
    cw.appendChild(cg);
    g3.appendChild(cw);
    body.appendChild(g3);
    var err = el('p', 'cf-err');
    err.id = 'cf-you-err';
    body.appendChild(err);

    function field(id, label, type, key, ph, optional) {
      var f = el('div', 'cf-field');
      f.innerHTML = '<label for="' + id + '">' + esc(label) + (optional ? ' <span class="opt">(optional)</span>' : '') + '</label>';
      var inp = document.createElement('input');
      inp.type = type; inp.id = id; inp.className = 'ps-input'; inp.placeholder = ph;
      inp.value = state[key];
      inp.autocomplete = key === 'name' ? 'given-name' : key;
      inp.addEventListener('input', function () { state[key] = inp.value; saveSoon(); refreshNav(); });
      f.appendChild(inp);
      return f;
    }
  }

  function buildReview(body) {
    var r = el('div', 'cf-review');
    if (CFG.preset) {
      var pg = el('div', 'cf-rgroup');
      var phead = el('div', 'rhead');
      phead.appendChild(el('strong', null, 'Starting design'));
      pg.appendChild(phead);
      pg.appendChild(el('div', 'rbody',
        '<span class="cf-rthumbs"><img src="' + esc(CFG.preset.image) + '" alt="' + esc(CFG.preset.alt || CFG.preset.name) + '"></span>' +
        '<b>' + esc(CFG.preset.name) + '</b>'));
      r.appendChild(pg);
    }
    r.appendChild(group(CFG.preset ? 'Your changes' : 'Your idea', 0, ideaHTML()));
    var det = [];
    (CFG.details || []).forEach(function (q) {
      var v = (state.details[q.key] || []).filter(function (x) { return x !== 'Not sure yet'; });
      if (v.length) det.push(esc(q.label) + ': <b>' + esc(v.join(', ')) + '</b>');
    });
    r.appendChild(group('Helpful details', 1, det.length ? det.join('<br>') : '<span class="muted">Skipped: our designers will guide you.</span>'));
    r.appendChild(group('Budget', 2, state.budget ? esc(state.budget) : '<span class="muted">Skipped: we’ll talk through what’s possible.</span>'));
    var tl = [state.occasion && esc(state.occasion), state.date && ('Ready by <b>' + esc(state.date) + '</b>')].filter(Boolean).join(' &middot; ');
    r.appendChild(group('Timeline', 3, tl || '<span class="muted">No date: we’ll move at your pace.</span>'));
    r.appendChild(group('You', 4, esc(state.name) + ' &middot; ' + esc(state.email) + (state.phone ? ' &middot; ' + esc(state.phone) : '') + ' &middot; prefers ' + esc(state.contact)));
    body.appendChild(r);
    body.appendChild(el('p', 'cf-submit-note', 'Sending your idea starts a conversation, not an order. Nothing is made, and nothing is charged, until you approve a design.'));

    function ideaHTML() {
      var h = state.idea ? esc(state.idea)
        : (CFG.preset && !state.images.length
          ? '<span class="muted">No changes. Make it just as shown.</span>'
          : '<span class="muted">Told with photos.</span>');
      if (state.images.length) {
        h += '<span class="cf-rthumbs">' + state.images.map(function (im) {
          return im.data ? '<img src="' + im.data + '" alt="' + esc(im.name) + '">' : '';
        }).join('') + '</span>';
      }
      if (state.matchImages.length) h += '<br><span class="muted">+ ' + state.matchImages.length + ' photo(s) of the piece to match</span>';
      return h;
    }
    function group(title, stepIdx, html) {
      var g = el('div', 'cf-rgroup');
      var head = el('div', 'rhead');
      head.appendChild(el('strong', null, esc(title)));
      var edit = el('button', 'redit', 'Edit');
      edit.type = 'button';
      edit.addEventListener('click', function () { go(stepIdx); });
      head.appendChild(edit);
      g.appendChild(head);
      g.appendChild(el('div', 'rbody', html));
      return g;
    }
  }

  /* ---------------- copy per step ---------------- */
  function stepCopy(id) {
    var cat = CFG.categoryLabel || 'piece';
    var map = {
      idea: {
        t: (CFG.step1 && CFG.step1.title) || 'Show us what’s in your head.',
        h: (CFG.step1 && CFG.step1.help) || 'A photo, a screenshot, a sketch, or a few words: whatever you have is enough to start. You don’t need to know anything about how jewelry is made.'
      },
      details: {
        t: 'A few details help our first sketch.',
        h: 'Answer what you know, skip what you don’t. “Not sure yet” is a perfectly good answer. That’s what our designers are for.'
      },
      budget: {
        t: 'Let’s make it work for your budget.',
        h: 'Your budget guides the design (stone sizes, metal, detail); it never limits the idea. As the manufacturer, we can usually do more for the money than you’d expect.'
      },
      timeline: {
        t: 'When do you need your ' + cat + '?',
        h: 'Your free 3D design is usually back in 2 business days. Once you approve it, making your piece takes 7 to 9 days. US shipping adds 1 to 3 business days on top. Tell us your date and we’ll plan backwards from it.'
      },
      you: {
        t: 'Where should the design go?',
        h: 'A real designer, not an auto-responder, reviews every request and replies personally.'
      },
      review: {
        t: 'Look right? Send it over.',
        h: 'You can edit any part before it goes to the design team.'
      }
    };
    return map[id];
  }

  /* ---------------- render shell ---------------- */
  root.classList.add('cf');
  var progress = el('div', 'cf-progress');
  progress.setAttribute('aria-hidden', 'true');
  PHASES.forEach(function (p) {
    var ph = el('div', 'cf-phase');
    ph.innerHTML = '<span class="bar"><i></i></span><span class="lbl">' + esc(p) + '</span>';
    progress.appendChild(ph);
  });
  root.appendChild(progress);
  var live = el('p');
  live.className = 'cust26-visually-hidden';
  live.setAttribute('aria-live', 'polite');
  live.style.position = 'absolute'; live.style.width = '1px'; live.style.height = '1px';
  live.style.overflow = 'hidden'; live.style.clipPath = 'inset(50%)';
  root.appendChild(live);

  var stepHost = el('div');
  root.appendChild(stepHost);

  var nav = el('div', 'cf-nav');
  var back = el('button', 'cf-back', '← Back');
  back.type = 'button';
  var nextWrap = el('div', 'cf-next-wrap');
  var next = el('button', 'ps-btn', 'CONTINUE');
  next.type = 'button';
  var skip = el('button', 'cf-skip', 'Skip this step');
  skip.type = 'button';
  var fast = el('button', 'cf-fast', 'In a hurry? <em>Send just this →</em>');
  fast.type = 'button';
  nextWrap.appendChild(next);
  nextWrap.appendChild(skip);
  nextWrap.appendChild(fast);
  nav.appendChild(back);
  nav.appendChild(nextWrap);
  root.appendChild(nav);

  back.addEventListener('click', function () { if (state.step > 0) go(state.step - 1); });
  skip.addEventListener('click', function () { go(state.step + 1); });
  fast.addEventListener('click', function () { go(4); });
  next.addEventListener('click', function () {
    var id = STEPS[state.step].id;
    if (id === 'idea' && !ideaValid()) {
      document.getElementById('cf-idea-err').textContent = 'Give us one thing to work with: a photo or a few words, whichever is easier.';
      settle(document.getElementById('cf-idea-err'));
      return;
    }
    if (id === 'you' && !youValid()) {
      document.getElementById('cf-you-err').textContent = 'We need your name and a valid email to send the design back to you.';
      settle(document.getElementById('cf-you-err'));
      return;
    }
    if (id === 'review') { submit(); return; }
    go(state.step + 1);
  });

  function refreshNav() {
    var id = STEPS[state.step].id;
    back.hidden = state.step === 0;
    skip.hidden = !(id === 'details' || id === 'budget' || id === 'timeline');
    fast.hidden = !(id === 'idea' && ideaValid());
    next.textContent = id === 'review' ? 'SEND MY IDEA' : 'CONTINUE';
    if (id === 'review') next.classList.add('ps-btn--brown'); else next.classList.remove('ps-btn--brown');
  }

  function renderProgress() {
    var phase = STEPS[state.step].phase;
    var detailsDone = state.step > 3;
    Array.prototype.forEach.call(progress.children, function (node, i) {
      node.className = 'cf-phase';
      if (i < phase) node.classList.add('done');
      if (i === phase) node.classList.add('now');
      if (i === 1 && phase === 1) {
        /* Details fills across its three inner steps */
        node.classList.add(state.step === 1 ? 'part' : 'now');
      }
      if (i === 1 && detailsDone) node.classList.add('done');
    });
  }

  function go(n) {
    state.step = Math.max(0, Math.min(STEPS.length - 1, n));
    save();
    render();
    settle(stepHost);
  }

  /* Put the target just under the sticky progress strip. Steps differ in height,
     so keeping the old scroll position after a CONTINUE dropped phones somewhere
     random in the next step (owner 2026-09-18). */
  function settle(target) {
    if (!target) return;
    var stuck = getComputedStyle(progress).position === 'sticky' ? progress.getBoundingClientRect().height : 0;
    var hdr = document.querySelector('.ps-sticky');
    var top = target.getBoundingClientRect().top + window.pageYOffset - stuck - 12;
    /* the compact header switches on past the page header; after this scroll it will be on */
    if (hdr && top > (document.querySelector('.ps-header') || { offsetHeight: 180 }).offsetHeight + 40) top -= hdr.offsetHeight;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
  }

  function render() {
    var id = STEPS[state.step].id;
    var copy = stepCopy(id);
    root.setAttribute('data-cf-step', id);
    root.setAttribute('data-cf-phase', String(STEPS[state.step].phase));
    stepHost.innerHTML = '';
    var s = el('section', 'cf-step cf-in');
    s.setAttribute('aria-label', copy.t);
    var h = el('h3', 'cf-title', esc(copy.t));
    h.setAttribute('tabindex', '-1');
    s.appendChild(h);
    s.appendChild(el('p', 'cf-help', esc(copy.h)));
    var body = el('div', 'cf-body');
    s.appendChild(body);
    ({ idea: buildIdea, details: buildDetails, budget: buildBudget, timeline: buildTimeline, you: buildYou, review: buildReview })[id](body);
    stepHost.appendChild(s);
    renderProgress();
    refreshNav();
    live.textContent = 'Step: ' + copy.t;
    if (resumed) h.focus({ preventScroll: true });
    resumed = true;
  }

  /* ---------------- submit + done ---------------- */
  /* The request ids are minted when the contact step first opens and kept in
     the draft, so a reload-and-resend reuses them: Meta and Google count one
     lead per event_id (lead tracking, 2026-10-12). */
  function ensureIds() {
    if (state.ref && state.eventId) return;
    var ids = (window.PSCustLead && window.PSCustLead.newIds)
      ? window.PSCustLead.newIds()
      : { ref: 'PS-C-' + Date.now().toString(36).toUpperCase().slice(-6), eventId: '' };
    state.ref = ids.ref;
    state.eventId = ids.eventId;
    save();
  }

  var sending = false, sendErr = null;
  function submit() {
    if (sending) return;
    sending = true;
    if (sendErr) { sendErr.remove(); sendErr = null; }
    next.disabled = true;
    next.innerHTML = '<span class="spin" aria-hidden="true"></span>SENDING…';
    ensureIds();
    var payload = {
      ref: state.ref, eventId: state.eventId, draftKey: KEY, awaitResult: true,
      category: CFG.category, design: CFG.preset ? CFG.preset.slug : undefined, state: state
    };
    var result = null;
    if (typeof CFG.onSubmit === 'function') {
      try { result = CFG.onSubmit(payload); } catch (e) { result = null; }
    }
    /* No fake "done": with a transport that reports back, the done screen waits
       for the answer. Until 2026-10-12 it appeared 800 ms after SEND whatever
       happened, and a failed send still told the shopper it had arrived. */
    if (result && typeof result.then === 'function') {
      result.then(function (res) { if (res && res.ok) showDone(state.ref); else sendFailed(); }, sendFailed);
    } else {
      /* a page without a reporting transport (local mockups) keeps the old pacing */
      setTimeout(function () { showDone(state.ref); }, REDUCED ? 100 : 800);
    }
  }

  function sendFailed() {
    sending = false;
    save();
    next.disabled = false;
    refreshNav();
    if (sendErr) sendErr.remove();
    sendErr = el('div', 'cf-resume');
    sendErr.setAttribute('role', 'alert');
    sendErr.innerHTML = '<p>We couldn’t send your design. Your answers are saved on this device.</p>';
    var acts = el('div', 'acts');
    var again = el('button', null, 'TRY AGAIN');
    again.type = 'button';
    again.addEventListener('click', submit);
    /* the resume bar's own quiet button style, so no new CSS */
    var ask = el('button', 'quiet', 'Questions? Contact us');
    ask.type = 'button';
    ask.addEventListener('click', function () { window.location.href = '/pages/contact-us'; });
    acts.appendChild(again);
    acts.appendChild(ask);
    sendErr.appendChild(acts);
    root.insertBefore(sendErr, nav);
    live.textContent = 'We couldn’t send your design. Your answers are saved on this device.';
    settle(sendErr);
  }

  function showDone(ref) {
    sending = false;
    try {
      var subs = JSON.parse(localStorage.getItem('ps-custom:submissions') || '[]');
      subs.push({ ref: ref, category: CFG.category, design: CFG.preset ? CFG.preset.slug : undefined, designName: CFG.preset ? CFG.preset.name : undefined, at: new Date().toISOString(), state: { idea: state.idea, images: state.images.length, budget: state.budget, occasion: state.occasion, date: state.date, name: state.name, email: state.email } });
      localStorage.setItem('ps-custom:submissions', JSON.stringify(subs));
      localStorage.removeItem(KEY);
    } catch (e) {}
    if (sendErr) { sendErr.remove(); sendErr = null; }
    progress.hidden = true;
    nav.hidden = true;
    stepHost.innerHTML = '';
    var d = el('div', 'cf-done');
    d.innerHTML =
      '<div class="mark"><svg viewBox="0 0 24 24" fill="none" stroke-width="2" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>' +
      '<h3 class="cf-title">Your idea is with our design team.</h3>' +
      '<p class="ref">Reference <b>' + ref + '</b> &middot; a designer replies to ' + esc(state.email || 'your inbox') + '</p>' +
      '<div class="cf-next-steps">' +
      '<div><strong>A designer reads your request</strong><p>Within 1 to 2 business days. A real person, thinking about your piece.</p></div>' +
      '<div><strong>Your free 3D design arrives</strong><p>A photoreal render of your exact piece, usually within 2 business days. Free, no obligation.</p></div>' +
      '<div><strong>You approve the design</strong><p>We refine round after round until you say it’s right. Nothing is made before that.</p></div>' +
      '<div><strong>We craft it in our US factory</strong><p>Made start to finish in 7 to 9 days in our own US factory, then shipped free and insured within the US.</p></div>' +
      '</div>';
    stepHost.appendChild(d);
    d.querySelector('.cf-title').setAttribute('tabindex', '-1');
    d.querySelector('.cf-title').focus({ preventScroll: true });
    settle(d);
    live.textContent = 'Request sent. Reference ' + ref;
  }

  /* ---------------- resume ---------------- */
  var saved = null;
  try { saved = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) {}
  /* A host page (the custom hub's hero panel) can hand over a seeded state: resume silently, no banner. */
  if (CFG.seed && CFG.seed._handoff) saved = CFG.seed;
  var handoff = !!(saved && saved._handoff);
  if (handoff) {
    Object.keys(state).forEach(function (k) { if (k in saved) state[k] = saved[k]; });
    state.images = (saved.images || []).filter(function (im) { return im.data; });
    state.matchImages = (saved.matchImages || []).filter(function (im) { return im.data; });
    resumed = true;
  } else if (saved && (saved.idea || (saved.images && saved.images.length) || saved.step > 0)) {
    var bar = el('div', 'cf-resume');
    bar.innerHTML = '<p>Pick up where you left off? Your earlier answers are saved on this device.</p>';
    var acts = el('div', 'acts');
    var res = el('button', null, 'Resume');
    res.type = 'button';
    var fresh = el('button', 'quiet', 'Start fresh');
    fresh.type = 'button';
    acts.appendChild(res); acts.appendChild(fresh);
    bar.appendChild(acts);
    root.insertBefore(bar, progress);
    res.addEventListener('click', function () {
      Object.keys(state).forEach(function (k) { if (k in saved) state[k] = saved[k]; });
      state.images = (saved.images || []).filter(function (im) { return im.data; });
      state.matchImages = (saved.matchImages || []).filter(function (im) { return im.data; });
      bar.remove();
      if (saved._thumbsDropped) live.textContent = 'Your photos could not be restored. Please re-add them.';
      go(saved.step || 0);
    });
    fresh.addEventListener('click', function () {
      try { localStorage.removeItem(KEY); } catch (e) {}
      bar.remove();
    });
  }

  if (handoff) go(saved.step || 1); else render();
})();
